import { useMemo } from 'react';

import { Box } from '@mui/material';

import { ChartProvider } from '@/components/Chart/context/ChartContext';
import useChartOptions from '@/components/Chart/hooks/options/useChartOptions';
import useSpotOptions from '@/components/Chart/hooks/options/useSpotOptions';
import useChartSeries from '@/components/Chart/hooks/useChartSeries';
import RegularChart from '@/components/Chart/RegularChart';
import type { PreviewChartProps } from '@/features/DataHub/constants/charts';
import { CHART_HEIGHT } from '@/features/DataHub/constants/charts';
import { DATAHUB_DEFAULT_GROUP } from '@/features/DataHub/constants/const';
import { useExport } from '@/features/DataHub/modules/Export/context/ExportContext';
import type { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

const PreviewSpotChart = ({ type }: PreviewChartProps) => {
  const { uniqueSymbols } = useExport();
  const templateChartSeries: TemplateChartSeries[] = useMemo(() => {
    return Array.from(uniqueSymbols)
      .slice(0, 6)
      .map(symbol => ({
        settings: { type: 'line' as const },
        symbol: {
          group: DATAHUB_DEFAULT_GROUP,
          key: {
            Code: symbol.Code,
            PeriodType: symbol.PeriodType,
            Period: symbol.Period,
            TimeRef: symbol.TimeRef
          }
        }
      }));
  }, [uniqueSymbols]);

  const { loading, error, chartSeries } = useChartSeries(templateChartSeries, true);

  const { createOptions } = useSpotOptions();
  const { options: baseOptions } = useChartOptions(chartSeries, createOptions);

  const options = useMemo(() => {
    if (!baseOptions) return undefined;
    return {
      ...baseOptions,
      chart: {
        ...baseOptions.chart,
        height: CHART_HEIGHT
      },
      navigator: {
        enabled: false
      },
      rangeSelector: {
        enabled: false
      },
      scrollbar: {
        enabled: false
      }
    };
  }, [baseOptions]);

  return (
    <ChartProvider>
      <Box sx={{ minHeight: CHART_HEIGHT, position: 'relative' }}>
        <RegularChart
          chartSeries={chartSeries}
          chartType={type}
          error={error}
          loading={loading}
          options={options}
          showLegend={false}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1
          }}
        />
      </Box>
    </ChartProvider>
  );
};

export default PreviewSpotChart;
